<script setup lang="ts">
import {
  computed,
  onMounted,
} from 'vue';
import { CdrText } from '@rei/cedar';
import HoursComponent from './HoursComponent.vue';
import ContactUsSlider from './ContactUsSlider.vue';
import ContactUsTextSection from './ContactUsTextSection.vue';
import { emailResponseTimeDefaultVal } from '../../common/shared';
import type {
  CUContentComponentProps,
  HoursModel,
} from './types';
import type { ContactUsSectionModel } from '../../api/interfaces/contactUsSectionModel';
import { useFetchSections } from './composables/useFetchSections';
import { useSectionInteractions } from './composables/useSectionInteractions';

const props = withDefaults(defineProps<CUContentComponentProps>(), {
  title: 'Contact Us',
  className: '',
  id: '',
  retailStoreHoursEvents: () => [],
  emailResponseTime: emailResponseTimeDefaultVal,
  helpCenterNumbers: () => ({}),
  isContactUsPage: false,
});

const {
  contactUsSections,
  isLoading,
  fetchContactUsSections,
} = useFetchSections();

const { handleSectionClick } = useSectionInteractions(props.title);

const hoursModel = computed((): HoursModel => ({
  retailStoreHoursEvents: props.retailStoreHoursEvents,
}));

// filtering sections by display type
const largeSections = computed(() => contactUsSections.value.filter((section) => section.display === 'large'));

const smallSections = computed(() => contactUsSections.value.filter((section) => section.display === 'small'));

// handle section click events
const sectionClick = (section: ContactUsSectionModel, phoneNumber?: { tel: string, callPhone }) => {
  handleSectionClick(section, phoneNumber);
};

onMounted(async () => {
  await fetchContactUsSections();
});
</script>
<template>
  <div
    :id="id"
    :class="className"
  >
    <CdrText
      v-if="!isContactUsPage"
      tag="h2"
      tabindex="0"
    >
      <span class="contact-us-header-title">
        Contact Us
      </span>
    </CdrText>
    <HoursComponent :hours-model="hoursModel" />

    <!-- loading -->
    <div
      v-if="isLoading"
      class="contact-us-loading"
    >
      <CdrText>Loading...</CdrText>
    </div>

    <!-- content when loaded -->
    <div v-else>
      <!-- large sections: cards slider -->
      <ContactUsSlider
        v-if="largeSections.length > 0"
        :sections="largeSections"
        :help-center-numbers="helpCenterNumbers"
        @section-click="sectionClick"
      />

      <!-- small sections: text-based -->
      <div
        v-if="smallSections.length > 0"
        class="contact-us-small-sections"
      >
        <ContactUsTextSection
          v-for="section in smallSections"
          :key="section.section"
          :section="section"
          :help-center-numbers="helpCenterNumbers"
          @section-click="sectionClick"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  @import 'src/main/style/contactUs.scss';
</style>
